import React from "react";
import { MessageCircle } from "lucide-react";

/** Set when the customer taps "Negotiate" on a pending order (day-to-day or relocation). */
export interface NegotiationRequest {
  requested_at: string;
  message: string | null;
  // The price the customer offered when they asked.
  proposed_price?: string | null;
}

export const NEGOTIATION_HINT = "The customer asked to negotiate. Send a counter offer first.";

const formatNaira = (value?: string | number | null) =>
  new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN", maximumFractionDigits: 0 }).format(Number(value ?? 0));

/** What to tell staff while the request is open, depending on whether the customer named a price. */
export const negotiationHint = (request?: NegotiationRequest | null) =>
  request?.proposed_price
    ? `The customer offered ${formatNaira(request.proposed_price)}. Accept it, send a counter offer, or reject.`
    : NEGOTIATION_HINT;

/**
 * While the request is open the backend refuses to accept the order at its
 * original price: the company has to counter first. After the counter, the
 * normal negotiation statuses take over.
 */
export const isNegotiationRequestOpen = (order?: {
  status?: string;
  negotiation_request?: NegotiationRequest | null;
} | null) => order?.status === "pending" && !!order?.negotiation_request;

const formatRequestedAt = (value?: string | null) => {
  if (!value) return null;
  const date = new Date(value.replace(" ", "T"));
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString([], { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" });
};

// The request stays on the order after agreement; it only "wants to
// negotiate" while no price has been agreed yet.
const NEGOTIATING_STATUSES = new Set(["pending", "counter_sent", "customer_countered", "customer_accepted"]);

/** Show the "Customer wants to negotiate" badge: there's a request and no price agreed yet. */
export const showNegotiationBadge = (order?: {
  status?: string;
  negotiation_request?: NegotiationRequest | null;
} | null) => !!order?.negotiation_request && NEGOTIATING_STATUSES.has(order?.status ?? "");

export const NegotiationRequestBadge: React.FC<{ className?: string; past?: boolean }> = ({ className = "", past = false }) => (
  <span
    className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold ${
      past ? "bg-gray-100 text-gray-600" : "bg-purple-100 text-purple-800"
    } ${className}`}
  >
    <MessageCircle size={11} aria-hidden="true" />
    {past ? "Customer asked to negotiate" : "Customer wants to negotiate"}
  </span>
);

/**
 * The customer's request on an order's detail view: their message (if any)
 * and when they asked. The "counter first" hint sits with the action buttons.
 */
export const NegotiationRequestNotice: React.FC<{
  request: NegotiationRequest;
  // A price has been agreed (or the order closed): show it as history.
  past?: boolean;
  className?: string;
}> = ({ request, past = false, className = "" }) => {
  const askedAt = formatRequestedAt(request.requested_at);
  return (
    <div className={`rounded-lg border p-4 ${past ? "border-gray-100 bg-gray-50" : "border-purple-200 bg-purple-50/60"} ${className}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <NegotiationRequestBadge past={past} />
        {askedAt && <span className="text-[11px] text-gray-500">Asked {askedAt}</span>}
      </div>
      {/* Kept after later rounds: only the latest counter from each side is stored elsewhere. */}
      {request.proposed_price && (
        <p className="mt-2 text-sm text-gray-700">
          Customer's first offer: <span className="font-semibold tabular-nums text-purple-800">{formatNaira(request.proposed_price)}</span>
        </p>
      )}
      {request.message ? (
        <p className="mt-2 max-w-prose whitespace-pre-line break-words rounded-lg rounded-tl-none border border-purple-100 bg-white px-3 py-2 text-sm text-gray-700">
          “{request.message}”
        </p>
      ) : (
        <p className="mt-2 text-xs italic text-gray-500">The customer didn't add a message.</p>
      )}
    </div>
  );
};
